import { useEffect, useMemo, useRef, useState } from "react";
import {
    useParams,
    useSearchParams,
} from "react-router-dom";
import axios from "axios";
import { useMaterialUIController, setLayout } from "context";

// MUI icons
import {
    Business,
    CheckCircle,
    CloudUpload,
    Description,
    ErrorOutline,
    InsertDriveFile,
    Language,
    Person,
    Replay,
    Send,
    WarningAmber,
} from "@mui/icons-material";

// MUI
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from "@mui/material";

// ============================================================
// CONFIG
// ============================================================

const API_URL =
    process.env.REACT_APP_API_URL ||
    "http://localhost:8080/api";

// ============================================================
// COLORS
// ============================================================

const COLORS = {
    navy: "#0B1F3A",
    navyDark: "#071426",

    blue: "#175CD3",
    blueLight: "#EFF8FF",

    green: "#027A48",
    greenStrong: "#039855",
    greenLight: "#ECFDF3",

    red: "#B42318",
    redStrong: "#D92D20",
    redLight: "#FEF3F2",

    amber: "#B54708",
    amberLight: "#FFFAEB",

    purple: "#6941C6",
    purpleLight: "#F4F3FF",

    text: "#101828",
    subtext: "#667085",
    muted: "#98A2B3",

    border: "#E4E7EC",
    borderDark: "#D0D5DD",

    page: "#F5F7FA",
    white: "#FFFFFF",
};

// ============================================================
// TRANSLATIONS
// ============================================================

const TEXTS = {
    tr: {
        title:
            "Cari Hesap Mutabakatı",

        subtitle:
            "Lütfen kayıtlarınızı kontrol ederek mutabakat durumunuzu bildiriniz.",

        reconciliationNo:
            "Mutabakat No",

        balanceDate:
            "Bakiye Tarihi",

        sentAt:
            "Gönderilme Zamanı",

        counterparty:
            "Cari Hesap",

        filenders:
            "Filenders",

        company:
            "Firma",

        taxNumber:
            "Vergi Numarası",

        taxOffice:
            "Vergi Dairesi",

        phone:
            "Telefon",

        email:
            "E-posta",

        address:
            "Adres",

        currentBalance:
            "Güncel Bakiye",

        currency:
            "Para Birimi",

        responseTitle:
            "Mutabakat Cevabınız",

        responseSubtitle:
            "Mutabakat durumunuzu seçerek gerekli bilgileri doldurunuz.",

        agreed:
            "Mutabıkız",

        disputed:
            "Mutabık Değiliz",

        agreedDescription:
            "Kayıtlarımız ile bildirilen bakiye uyumludur.",

        disputedDescription:
            "Kayıtlarımız ile bildirilen bakiye arasında fark bulunmaktadır.",

        responder:
            "Cevaplayan",

        responderPlaceholder:
            "Ad Soyad",

        amount:
            "Mutabık Olunmayan Tutar",

        balanceType:
            "Bakiye Türü",

        debit:
            "Borç",

        credit:
            "Alacak",

        explanation:
            "Açıklama",

        explanationPlaceholder:
            "Mutabık olmama nedeninizi ve kayıtlarınızdaki durumu açıklayınız.",

        document:
            "Doküman",

        selectDocument:
            "Dosya Seç",

        optional:
            "Opsiyonel",

        required:
            "Zorunlu",

        documentRequired:
            "Mutabık olmadığınız durumda belge yüklemeniz zorunludur.",

        selectedDocument:
            "Seçilen Dosya",

        documentUploadPending:
            "Seçtiğiniz belge mutabakat cevabınızla birlikte gönderilecektir.",

        submit:
            "Cevabı Gönder",

        sending:
            "Gönderiliyor...",

        loading:
            "Mutabakat bilgileri yükleniyor...",

        loadError:
            "Mutabakat bilgileri alınamadı.",

        invalidLink:
            "Mutabakat bağlantısı geçersiz veya süresi dolmuş olabilir.",

        responseRequired:
            "Lütfen Mutabıkız veya Mutabık Değiliz seçeneklerinden birini seçiniz.",

        responderRequired:
            "Cevaplayan kişi zorunludur.",

        amountRequired:
            "Mutabık olmadığınız durumda tutar bilgisi zorunludur.",

        balanceTypeRequired:
            "Borç veya Alacak seçimi zorunludur.",

        explanationRequired:
            "Mutabık olmadığınız durumda açıklama zorunludur.",

        submitError:
            "Mutabakat cevabı gönderilemedi.",

        agreedSuccess:
            "Mutabakat cevabınız başarıyla iletildi. Mutabık olduğunuzu bildirdiniz.",

        disputedSuccess:
            "Mutabakat cevabınız başarıyla iletildi. Mutabık olmadığınızı bildirdiniz.",

        thankYou:
            "Teşekkür Ederiz",

        recorded:
            "Cevabınız sistemimize kaydedilmiştir.",

        answeredBy:
            "Cevaplayan",

        answeredAt:
            "Cevap Zamanı",

        answer:
            "Cevap",

        previousResponse:
            "Bu mutabakat için daha önce cevap verilmiştir.",

        responseHistory:
            "Cevap Geçmişi",

        noHistory:
            "Henüz geçmiş cevap bulunmuyor.",

        retry:
            "Tekrar Dene",

        secure:
            "Güvenli Mutabakat Ekranı",

        privacy:
            "Bu bağlantı yalnızca ilgili mutabakata erişim sağlar.",

        language:
            "Dil",

        turkish:
            "Türkçe",

        english:
            "İngilizce",
    },

    en: {
        title:
            "Account Reconciliation",

        subtitle:
            "Please review your records and submit your reconciliation response.",

        reconciliationNo:
            "Reconciliation No",

        balanceDate:
            "Balance Date",

        sentAt:
            "Sent At",

        counterparty:
            "Account",

        filenders:
            "Filenders",

        company:
            "Company",

        taxNumber:
            "Tax Number",

        taxOffice:
            "Tax Office",

        phone:
            "Phone",

        email:
            "Email",

        address:
            "Address",

        currentBalance:
            "Current Balance",

        currency:
            "Currency",

        responseTitle:
            "Your Reconciliation Response",

        responseSubtitle:
            "Select your reconciliation status and complete the required information.",

        agreed:
            "Agree",

        disputed:
            "Disagree",

        agreedDescription:
            "Our records agree with the stated balance.",

        disputedDescription:
            "There is a difference between our records and the stated balance.",

        responder:
            "Responded By",

        responderPlaceholder:
            "Full Name",

        amount:
            "Unreconciled Amount",

        balanceType:
            "Balance Type",

        debit:
            "Debit",

        credit:
            "Credit",

        explanation:
            "Explanation",

        explanationPlaceholder:
            "Please explain the difference and the balance shown in your records.",

        document:
            "Document",

        selectDocument:
            "Select File",

        optional:
            "Optional",

        required:
            "Required",

        documentRequired:
            "A supporting document is required when you disagree.",

        selectedDocument:
            "Selected File",

        documentUploadPending:
            "The selected document will be submitted with your reconciliation response.",

        submit:
            "Submit Response",

        sending:
            "Sending...",

        loading:
            "Loading reconciliation information...",

        loadError:
            "Reconciliation information could not be loaded.",

        invalidLink:
            "The reconciliation link may be invalid or expired.",

        responseRequired:
            "Please select Agree or Disagree.",

        responderRequired:
            "Responder name is required.",

        amountRequired:
            "Amount is required when you disagree.",

        balanceTypeRequired:
            "Debit or Credit selection is required.",

        explanationRequired:
            "Explanation is required when you disagree.",

        submitError:
            "Reconciliation response could not be submitted.",

        agreedSuccess:
            "Your reconciliation response was submitted successfully. You confirmed that you agree.",

        disputedSuccess:
            "Your reconciliation response was submitted successfully. You reported that you disagree.",

        thankYou:
            "Thank You",

        recorded:
            "Your response has been recorded.",

        answeredBy:
            "Responded By",

        answeredAt:
            "Response Time",

        answer:
            "Response",

        previousResponse:
            "A response has already been submitted for this reconciliation.",

        responseHistory:
            "Response History",

        noHistory:
            "No previous responses are available.",

        retry:
            "Try Again",

        secure:
            "Secure Reconciliation Page",

        privacy:
            "This link provides access only to the related reconciliation.",

        language:
            "Language",

        turkish:
            "Turkish",

        english:
            "English",
    },
};

// ============================================================
// COMMON STYLE
// ============================================================

const cardSx = {
    borderRadius: "10px",
    border: "1px solid rgba(15, 23, 42, 0.12)",

    boxShadow: "none",

    background:
    COLORS.white,
};

const inputSx = {
    "& .MuiOutlinedInput-root": {
        minHeight: "50px",
        height: "50px",
        borderRadius: "8px",
        backgroundColor: COLORS.white,
        color: COLORS.text,

        "& fieldset": {
            borderColor: COLORS.borderDark,
        },

        "&:hover fieldset": {
            borderColor: COLORS.blue,
        },

        "&.Mui-focused fieldset": {
            borderColor: COLORS.blue,
            borderWidth: "1.5px",
        },

        "&.Mui-disabled": {
            backgroundColor: "#F9FAFB",
        },
    },

    "& .MuiInputBase-input": {
        height: "50px",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        paddingTop: "0 !important",
        paddingBottom: "0 !important",
    },

    "& .MuiSelect-select": {
        height: "50px !important",
        minHeight: "50px !important",
        boxSizing: "border-box !important",
        display: "flex !important",
        alignItems: "center !important",
        paddingTop: "0 !important",
        paddingBottom: "0 !important",
    },

    "& .MuiInputLabel-root": {
        color: COLORS.subtext,
        backgroundColor: COLORS.white,
        paddingLeft: "4px",
        paddingRight: "4px",

        "&.Mui-focused": {
            color: COLORS.blue,
        },
    },

    "& .MuiSelect-icon": {
        color: COLORS.subtext,
    },
};
const multilineSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius:
            "8px",

        background:
        COLORS.white,
    },

    "& .MuiInputLabel-root": {
        background:
        COLORS.white,

        px:
            0.5,
    },
};

// ============================================================
// HELPERS
// ============================================================

const normalizeString = (
    value
) =>
    String(
        value ??
        ""
    ).trim();

const toNumber = (
    value
) => {
    const number =
        Number(
            value
        );

    return Number.isFinite(
        number
    )
        ? number
        : 0;
};

const getInitialLanguage = (
    searchParams
) => {
    const queryLanguage =
        searchParams.get(
            "lang"
        );

    if (
        queryLanguage ===
        "en"
    ) {
        return "en";
    }

    if (
        queryLanguage ===
        "tr"
    ) {
        return "tr";
    }

    if (
        typeof navigator !==
        "undefined"
    ) {
        const browserLanguage =
            String(
                navigator.language ||
                ""
            ).toLowerCase();

        if (
            browserLanguage.startsWith(
                "tr"
            )
        ) {
            return "tr";
        }
    }

    return "tr";
};

const formatDate = (
    value,
    language
) => {
    if (
        !value
    ) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleDateString(
        language ===
        "tr"
            ? "tr-TR"
            : "en-GB"
    );
};

const formatDateTime = (
    value,
    language
) => {
    if (
        !value
    ) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleString(
        language ===
        "tr"
            ? "tr-TR"
            : "en-GB",
        {
            dateStyle:
                "medium",

            timeStyle:
                "short",
        }
    );
};

const formatMoney = (
    value,
    currency,
    language
) => {
    try {
        return new Intl.NumberFormat(
                language ===
                "tr"
                    ? "tr-TR"
                    : "en-US",
                {
                    minimumFractionDigits:
                        2,

                    maximumFractionDigits:
                        2,
                }
            ).format(
                toNumber(
                    value
                )
            ) +
            ` ${currency || ""}`;
    } catch {
        return `${toNumber(value).toFixed(2)} ${currency || ""}`;
    }
};

const getErrorMessage = (
    error,
    fallback
) =>
    error
        ?.response
        ?.data
        ?.message ||
    fallback;

// ============================================================
// INFO ROW
// ============================================================

function InfoRow({
                     label,
                     value,
                 }) {
    return (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={2}
            py={1}
        >
            <Typography
                variant="caption"
                sx={{
                    color:
                    COLORS.subtext,

                    minWidth:
                        105,
                }}
            >
                {label}
            </Typography>

            <Typography
                variant="body2"
                fontWeight={600}
                textAlign="right"
                sx={{
                    color:
                    COLORS.text,

                    wordBreak:
                        "break-word",
                }}
            >
                {value || "-"}
            </Typography>
        </Box>
    );
}

// ============================================================
// COMPANY CARD
// ============================================================

function CompanyCard({
                         title,
                         company,
                         texts,
                         accent,
                     }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius:
                    "10px",

                overflow:
                    "hidden",

                borderColor:
                COLORS.border,

                height:
                    "100%",

                background:
                COLORS.white,
            }}
        >
            <Box
                px={2.5}
                py={2}
                display="flex"
                alignItems="center"
                gap={1.2}
                sx={{
                    background:
                        "#F8FAFC",

                    borderBottom:
                        `1px solid ${COLORS.border}`,
                }}
            >
                <Box
                    width={40}
                    height={40}
                    borderRadius="11px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        background:
                        accent.background,

                        color:
                        accent.color,
                    }}
                >
                    <Business />
                </Box>

                <Box>
                    <Typography
                        variant="caption"
                        sx={{
                            color:
                            COLORS.subtext,

                            fontWeight:
                                700,
                        }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        variant="subtitle1"
                        fontWeight={800}
                        sx={{
                            color:
                            COLORS.text,
                        }}
                    >
                        {company
                                ?.name ||
                            "-"}
                    </Typography>
                </Box>
            </Box>

            <Box
                p={2.5}
            >
                <InfoRow
                    label={
                        texts.taxNumber
                    }
                    value={
                        company
                            ?.taxNumber
                    }
                />

                <Divider />

                <InfoRow
                    label={
                        texts.taxOffice
                    }
                    value={
                        company
                            ?.taxOffice
                    }
                />

                <Divider />

                <InfoRow
                    label={
                        texts.phone
                    }
                    value={
                        company
                            ?.phone
                    }
                />

                {
                    company
                        ?.email ? (
                        <>
                            <Divider />

                            <InfoRow
                                label={
                                    texts.email
                                }
                                value={
                                    company
                                        .email
                                }
                            />
                        </>
                    ) : null
                }

                {
                    company
                        ?.address ? (
                        <>
                            <Divider />

                            <InfoRow
                                label={
                                    texts.address
                                }
                                value={
                                    company
                                        .address
                                }
                            />
                        </>
                    ) : null
                }
            </Box>
        </Paper>
    );
}

// ============================================================
// RESPONSE BUTTON
// ============================================================

function ResponseButton({
                            active,
                            type,
                            texts,
                            onClick,
                        }) {
    const agreed =
        type ===
        "agreed";

    const activeColor =
        agreed
            ? COLORS.greenStrong
            : COLORS.redStrong;

    const activeBackground =
        agreed
            ? COLORS.greenLight
            : COLORS.redLight;

    return (
        <Button
            fullWidth
            onClick={
                onClick
            }
            startIcon={
                agreed ? (
                    <CheckCircle />
                ) : (
                    <WarningAmber />
                )
            }
            sx={{
                minHeight:
                    58,

                borderRadius:
                    "13px",

                border:
                    `1px solid ${
                        active
                            ? activeColor
                            : COLORS.borderDark
                    }`,

                background:
                    active
                        ? activeBackground
                        : COLORS.white,

                color:
                    active
                        ? activeColor
                        : COLORS.text,

                fontWeight:
                    800,

                fontSize:
                    13,

                boxShadow:
                    active
                        ? "0 4px 12px rgba(16,24,40,.06)"
                        : "none",

                "&:hover": {
                    background:
                        active
                            ? activeBackground
                            : "#F9FAFB",

                    borderColor:
                    activeColor,
                },
            }}
        >
            {
                agreed
                    ? texts.agreed
                    : texts.disputed
            }
        </Button>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

function ReconciliationResponse() {
    // This page is opened by customers / suppliers from an email link, with no
    // account. App.js renders the internal sidebar whenever the layout is
    // "dashboard" (the default), so without this the counterparty saw the
    // TMS navigation shell around the reconciliation form.
    const [, uiDispatch] = useMaterialUIController();

    useEffect(() => {
        setLayout(uiDispatch, "page");
    }, [uiDispatch]);

    const {
        token,
    } =
        useParams();

    const [
        searchParams,
    ] =
        useSearchParams();

    const fileInputRef =
        useRef(null);

    const [
        language,
        setLanguage,
    ] =
        useState(
            () =>
                getInitialLanguage(
                    searchParams
                )
        );

    const texts =
        TEXTS[
            language
            ];

    const [
        reconciliation,
        setReconciliation,
    ] =
        useState(null);

    const [
        loading,
        setLoading,
    ] =
        useState(true);

    const [
        submitting,
        setSubmitting,
    ] =
        useState(false);

    const [
        fileUploading,
        setFileUploading,
    ] = useState(false);

    const [
        historyLoading,
        setHistoryLoading,
    ] =
        useState(false);

    const [
        history,
        setHistory,
    ] =
        useState([]);

    const [
        showHistory,
        setShowHistory,
    ] =
        useState(false);

    const [
        error,
        setError,
    ] =
        useState("");

    const [
        formError,
        setFormError,
    ] =
        useState("");

    const [
        success,
        setSuccess,
    ] =
        useState(null);

    const [
        responseType,
        setResponseType,
    ] =
        useState(
            () => {
                const response =
                    searchParams.get(
                        "response"
                    );

                if (
                    response ===
                    "agreed" ||
                    response ===
                    "disputed"
                ) {
                    return response;
                }

                return "";
            }
        );

    const [
        form,
        setForm,
    ] =
        useState({
            respondedBy:
                "",

            balance:
                "",

            balanceType:
                "debit",

            note:
                "",
        });

    const [
        selectedFile,
        setSelectedFile,
    ] =
        useState(null);

    // ========================================================
    // DOCUMENT TITLE
    // ========================================================

    useEffect(() => {
        document.title =
            language ===
            "tr"
                ? "Cari Hesap Mutabakatı"
                : "Account Reconciliation";
    }, [
        language,
    ]);

    // ========================================================
    // QUERY RESPONSE SYNC
    // ========================================================

    useEffect(() => {
        const response =
            searchParams.get(
                "response"
            );

        if (
            response ===
            "agreed" ||
            response ===
            "disputed"
        ) {
            setResponseType(
                response
            );
        }
    }, [
        searchParams,
    ]);

    // ========================================================
    // FETCH
    // ========================================================

    const fetchReconciliation =
        async () => {
            if (
                !token
            ) {
                setError(
                    texts.invalidLink
                );

                setLoading(
                    false
                );

                return;
            }

            try {
                setLoading(
                    true
                );

                setError(
                    ""
                );

                const response =
                    await axios.get(
                        `${API_URL}/finance/reconciliations/public/${encodeURIComponent(
                            token
                        )}`
                    );

                const data =
                    response
                        ?.data
                        ?.data ||
                    null;

                if (
                    !data
                ) {
                    throw new Error(
                        texts.loadError
                    );
                }

                setReconciliation(
                    data
                );
            } catch (
                requestError
                ) {
                console.error(
                    "Public reconciliation fetch error:",
                    requestError
                );

                setError(
                    getErrorMessage(
                        requestError,
                        texts.invalidLink
                    )
                );
            } finally {
                setLoading(
                    false
                );
            }
        };

    useEffect(() => {
        fetchReconciliation();
    }, [
        token,
    ]);

    // ========================================================
    // HISTORY
    // ========================================================

    const fetchHistory =
        async () => {
            if (
                !token
            ) {
                return;
            }

            try {
                setHistoryLoading(
                    true
                );

                const response =
                    await axios.get(
                        `${API_URL}/finance/reconciliations/public/${encodeURIComponent(
                            token
                        )}/history`
                    );

                setHistory(
                    response
                        ?.data
                        ?.data ||
                    []
                );

                setShowHistory(
                    true
                );
            } catch (
                requestError
                ) {
                console.error(
                    "Reconciliation history fetch error:",
                    requestError
                );
            } finally {
                setHistoryLoading(
                    false
                );
            }
        };

    // ========================================================
    // FORM
    // ========================================================

    const updateForm = (
        field,
        value
    ) => {
        setForm(
            (
                current
            ) => ({
                ...current,

                [field]:
                value,
            })
        );

        setFormError(
            ""
        );
    };

    const selectResponse = (
        type
    ) => {
        setResponseType(
            type
        );

        setFormError(
            ""
        );

        if (
            type ===
            "agreed"
        ) {
            setForm(
                (
                    current
                ) => ({
                    ...current,

                    balance:
                        "",

                    balanceType:
                        "debit",

                    note:
                        "",
                })
            );
        }
    };

    // ========================================================
    // FILE
    // ========================================================

    const handleFileChange = (
        event
    ) => {
        const file =
            event
                .target
                .files
                ?.[0] ||
            null;

        setSelectedFile(
            file
        );
    };

    const uploadSelectedFile =
        async () => {
            if (
                !selectedFile
            ) {
                return {
                    documentUrl:
                        "",

                    documentName:
                        "",
                };
            }

            const formData =
                new FormData();

            formData.append(
                "file",
                selectedFile
            );

            setFileUploading(
                true
            );

            try {
                const response =
                    await axios.post(
                        `${API_URL}/finance/reconciliations/public/${encodeURIComponent(
                            token
                        )}/upload`,
                        formData,
                        {
                            headers: {
                                "Content-Type":
                                    "multipart/form-data",
                            },
                        }
                    );

                return {
                    documentUrl:
                        response
                            ?.data
                            ?.data
                            ?.documentUrl ||
                        "",

                    documentName:
                        response
                            ?.data
                            ?.data
                            ?.documentName ||
                        selectedFile.name,
                };
            } finally {
                setFileUploading(
                    false
                );
            }
        };

    // ========================================================
    // VALIDATION
    // ========================================================

    const validateForm =
        () => {
            if (
                !responseType
            ) {
                return texts.responseRequired;
            }

            if (
                !normalizeString(
                    form.respondedBy
                )
            ) {
                return texts.responderRequired;
            }

            if (
                responseType ===
                "disputed"
            ) {
                if (
                    form.balance ===
                    "" ||
                    form.balance ===
                    null ||
                    form.balance ===
                    undefined
                ) {
                    return texts.amountRequired;
                }

                const amount =
                    Number(
                        form.balance
                    );

                if (
                    !Number.isFinite(
                        amount
                    )
                ) {
                    return texts.amountRequired;
                }

                if (
                    !form.balanceType
                ) {
                    return texts.balanceTypeRequired;
                }

                if (
                    !normalizeString(
                        form.note
                    )
                ) {
                    return texts.explanationRequired;
                }
                if (
                    !selectedFile
                ) {
                    return texts.documentRequired;
                }
            }

            return "";
        };

    // ========================================================
    // SUBMIT
    // ========================================================

    const handleSubmit =
        async () => {
            const validationError =
                validateForm();

            if (
                validationError
            ) {
                setFormError(
                    validationError
                );

                return;
            }

            try {
                setSubmitting(
                    true
                );

                setFormError(
                    ""
                );

                const uploadedDocument =
                    await uploadSelectedFile();

                const payload = {
                    response:
                    responseType,

                    respondedBy:
                        normalizeString(
                            form.respondedBy
                        ),

                    documentUrl:
                    uploadedDocument
                        .documentUrl,

                    documentName:
                    uploadedDocument
                        .documentName,
                };

                if (
                    responseType ===
                    "disputed"
                ) {
                    payload.balance =
                        Math.abs(
                            toNumber(
                                form.balance
                            )
                        );

                    payload.balanceType =
                        form.balanceType;

                    payload.note =
                        normalizeString(
                            form.note
                        );
                }

                const response =
                    await axios.post(
                        `${API_URL}/finance/reconciliations/public/${encodeURIComponent(
                            token
                        )}/respond`,
                        payload
                    );

                const responseData =
                    response
                        ?.data
                        ?.data ||
                    {};

                setSuccess({
                    type:
                    responseType,

                    respondedBy:
                        responseData
                            ?.respondedBy ||
                        form.respondedBy,

                    respondedAt:
                        responseData
                            ?.respondedAt ||
                        new Date(),
                });

                setReconciliation(
                    (
                        current
                    ) => ({
                        ...current,

                        response:
                        responseType,

                        respondedBy:
                            responseData
                                ?.respondedBy ||
                            form.respondedBy,

                        respondedAt:
                            responseData
                                ?.respondedAt ||
                            new Date(),

                        status:
                            responseData
                                ?.status ||
                            (
                                responseType ===
                                "agreed"
                                    ? "confirmed"
                                    : "disputed"
                            ),
                    })
                );
            } catch (
                requestError
                ) {
                console.error(
                    "Public reconciliation response submit error:",
                    requestError
                );

                setFormError(
                    getErrorMessage(
                        requestError,
                        texts.submitError
                    )
                );
            } finally {
                setSubmitting(
                    false
                );
            }
        };

    // ========================================================
    // DERIVED
    // ========================================================

    const currency =
        reconciliation
            ?.currency ||
        "EUR";

    const currentBalance =
        reconciliation
            ?.currentBalance ??
        0;

    const previousResponse =
        reconciliation
            ?.response &&
        reconciliation
            ?.response !==
        "pending";

    const company =
        reconciliation
            ?.company ||
        {};

    const party =
        reconciliation
            ?.party ||
        {};

    const resultConfig =
        useMemo(
            () => {
                const type =
                    success
                        ?.type ||
                    reconciliation
                        ?.response;

                if (
                    type ===
                    "agreed"
                ) {
                    return {
                        color:
                        COLORS.green,

                        background:
                        COLORS.greenLight,

                        icon:
                            <CheckCircle
                                sx={{
                                    fontSize:
                                        42,
                                }}
                            />,

                        label:
                        texts.agreed,
                    };
                }

                if (
                    type ===
                    "disputed"
                ) {
                    return {
                        color:
                        COLORS.red,

                        background:
                        COLORS.redLight,

                        icon:
                            <WarningAmber
                                sx={{
                                    fontSize:
                                        42,
                                }}
                            />,

                        label:
                        texts.disputed,
                    };
                }

                return null;
            },
            [
                success,
                reconciliation,
                texts,
            ]
        );

    // ========================================================
    // LOADING SCREEN
    // ========================================================

    if (
        loading
    ) {
        return (
            <Box
                minHeight="100vh"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                gap={2}
                sx={{
                    background:
                    COLORS.page,
                }}
            >
                <CircularProgress />

                <Typography
                    variant="body2"
                    sx={{
                        color:
                        COLORS.subtext,
                    }}
                >
                    {texts.loading}
                </Typography>
            </Box>
        );
    }

    // ========================================================
    // ERROR SCREEN
    // ========================================================

    if (
        error ||
        !reconciliation
    ) {
        return (
            <Box
                minHeight="100vh"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={2}
                sx={{
                    background:
                    COLORS.page,
                }}
            >
                <Card
                    sx={{
                        ...cardSx,

                        width:
                            "100%",

                        maxWidth:
                            560,
                    }}
                >
                    <Box
                        p={4}
                        textAlign="center"
                    >
                        <ErrorOutline
                            sx={{
                                fontSize:
                                    52,

                                color:
                                COLORS.red,
                            }}
                        />

                        <Typography
                            variant="h5"
                            mt={1}
                            fontWeight={800}
                        >
                            {texts.loadError}
                        </Typography>

                        <Typography
                            variant="body2"
                            mt={1}
                            sx={{
                                color:
                                COLORS.subtext,
                            }}
                        >
                            {error ||
                                texts.invalidLink}
                        </Typography>

                        <Button
                            variant="contained"
                            startIcon={
                                <Replay />
                            }
                            onClick={
                                fetchReconciliation
                            }
                            sx={{
                                mt: 3,

                                borderRadius:
                                    "10px",

                                background:
                                COLORS.blue,
                            }}
                        >
                            {texts.retry}
                        </Button>
                    </Box>
                </Card>
            </Box>
        );
    }

    // ========================================================
    // PAGE
    // ========================================================

    return (
        <Box
            minHeight="100vh"
            sx={{
                background:
                COLORS.page,

                py: {
                    xs:
                        2,

                    md:
                        4,
                },

                px: {
                    xs:
                        1.5,

                    md:
                        3,
                },
            }}
        >
            <Box
                width="100%"
                maxWidth={1180}
                mx="auto"
            >
                {/* ================================================= */}
                {/* HEADER */}
                {/* ================================================= */}

                <Card
                    sx={{
                        ...cardSx,

                        mb:
                            2.5,

                        overflow:
                            "hidden",
                    }}
                >
                    <Box
                        px={{
                            xs:
                                2.5,

                            md:
                                4,
                        }}
                        py={{
                            xs:
                                2.5,

                            md:
                                3.5,
                        }}
                        sx={{
                            background:
                                "#0B1F3A",

                            color:
                            COLORS.white,
                        }}
                    >
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems={{
                                xs:
                                    "flex-start",

                                md:
                                    "center",
                            }}
                            flexDirection={{
                                xs:
                                    "column",

                                md:
                                    "row",
                            }}
                            gap={2}
                        >
                            <Box>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <Description />

                                    <Typography
                                        variant="caption"
                                        fontWeight={800}
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",

                                            letterSpacing:
                                                ".08em",
                                        }}
                                    >
                                        {texts.secure}
                                    </Typography>
                                </Box>

                                <Typography
                                    variant="h4"
                                    mt={0.8}
                                    fontWeight={800}
                                    sx={{
                                        color:
                                        COLORS.white,
                                    }}
                                >
                                    {texts.title}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    mt={0.8}
                                    sx={{
                                        color:
                                            "rgba(255,255,255,.75)",
                                    }}
                                >
                                    {texts.subtitle}
                                </Typography>
                            </Box>

                            {/* LANGUAGE */}

                            {/* LANGUAGE */}

                            <FormControl
                                size="small"
                                sx={{
                                    width: {
                                        xs: "100%",
                                        sm: 170,
                                    },

                                    minWidth: 170,

                                    "& .MuiOutlinedInput-root": {
                                        height: "44px",
                                        minHeight: "44px",
                                        borderRadius: "8px",
                                        backgroundColor: COLORS.white,
                                        color: COLORS.text,

                                        "& fieldset": {
                                            borderColor: "rgba(255,255,255,0.35)",
                                        },

                                        "&:hover fieldset": {
                                            borderColor: COLORS.white,
                                        },

                                        "&.Mui-focused fieldset": {
                                            borderColor: COLORS.white,
                                            borderWidth: "1.5px",
                                        },
                                    },

                                    "& .MuiSelect-select": {
                                        height: "44px !important",
                                        minHeight: "44px !important",
                                        boxSizing: "border-box !important",

                                        display: "flex !important",
                                        alignItems: "center !important",

                                        paddingTop: "0 !important",
                                        paddingBottom: "0 !important",
                                        paddingLeft: "42px !important",
                                        paddingRight: "38px !important",

                                        color: `${COLORS.text} !important`,
                                        fontSize: "13px",
                                        fontWeight: 600,
                                    },

                                    "& .MuiSelect-icon": {
                                        color: `${COLORS.subtext} !important`,
                                        right: "10px",
                                    },
                                }}
                            >
                                <Select
                                    value={language}
                                    onChange={(event) =>
                                        setLanguage(
                                            event.target.value
                                        )
                                    }
                                    displayEmpty
                                    inputProps={{
                                        "aria-label":
                                        texts.language,
                                    }}
                                    startAdornment={
                                        <Language
                                            sx={{
                                                position: "absolute",
                                                left: "14px",
                                                zIndex: 1,
                                                pointerEvents: "none",

                                                fontSize: 19,

                                                color:
                                                    `${COLORS.blue} !important`,
                                            }}
                                        />
                                    }
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                mt: 0.8,
                                                borderRadius: "8px",

                                                border:
                                                    `1px solid ${COLORS.border}`,

                                                boxShadow:
                                                    "0 10px 30px rgba(16,24,40,0.14)",

                                                "& .MuiMenuItem-root": {
                                                    minHeight: "42px",
                                                    fontSize: "13px",
                                                    color: COLORS.text,

                                                    "&:hover": {
                                                        backgroundColor:
                                                        COLORS.blueLight,
                                                    },

                                                    "&.Mui-selected": {
                                                        color: COLORS.blue,
                                                        fontWeight: 700,
                                                        backgroundColor:
                                                        COLORS.blueLight,
                                                    },

                                                    "&.Mui-selected:hover": {
                                                        backgroundColor:
                                                        COLORS.blueLight,
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="tr">
                                        {texts.turkish}
                                    </MenuItem>

                                    <MenuItem value="en">
                                        {texts.english}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    {/* META */}

                    <Grid
                        container
                        spacing={0}
                    >
                        <Grid
                            item
                            xs={12}
                            md={4}
                        >
                            <Box
                                p={2.2}
                                sx={{
                                    borderRight: {
                                        md:
                                            `1px solid ${COLORS.border}`,
                                    },

                                    borderBottom: {
                                        xs:
                                            `1px solid ${COLORS.border}`,

                                        md:
                                            "none",
                                    },
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {texts.reconciliationNo}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    fontWeight={800}
                                    mt={0.4}
                                >
                                    {reconciliation
                                            ?.reconciliationNo ||
                                        "-"}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={4}
                        >
                            <Box
                                p={2.2}
                                sx={{
                                    borderRight: {
                                        md:
                                            `1px solid ${COLORS.border}`,
                                    },

                                    borderBottom: {
                                        xs:
                                            `1px solid ${COLORS.border}`,

                                        md:
                                            "none",
                                    },
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {texts.balanceDate}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    fontWeight={800}
                                    mt={0.4}
                                >
                                    {formatDate(
                                        reconciliation
                                            ?.balanceDate,
                                        language
                                    )}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={4}
                        >
                            <Box
                                p={2.2}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {texts.sentAt}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    fontWeight={800}
                                    mt={0.4}
                                >
                                    {formatDateTime(
                                        reconciliation
                                            ?.sentAt,
                                        language
                                    )}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>

                {/* ================================================= */}
                {/* COMPANY CARDS */}
                {/* ================================================= */}

                <Grid
                    container
                    spacing={2.5}
                    mb={2.5}
                >
                    <Grid
                        item
                        xs={12}
                        md={6}
                    >
                        <CompanyCard
                            title={
                                texts.counterparty
                            }
                            company={
                                party
                            }
                            texts={
                                texts
                            }
                            accent={{
                                background:
                                COLORS.blueLight,

                                color:
                                COLORS.blue,
                            }}
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={6}
                    >
                        <CompanyCard
                            title={
                                texts.filenders
                            }
                            company={
                                company
                            }
                            texts={
                                texts
                            }
                            accent={{
                                background:
                                COLORS.purpleLight,

                                color:
                                COLORS.purple,
                            }}
                        />
                    </Grid>
                </Grid>

                {/* ================================================= */}
                {/* BALANCE */}
                {/* ================================================= */}

                <Card
                    sx={{
                        ...cardSx,

                        mb:
                            2.5,
                    }}
                >
                    <Box
                        p={{
                            xs:
                                3,

                            md:
                                4,
                        }}
                        textAlign="center"
                    >
                        <Typography
                            variant="caption"
                            fontWeight={800}
                            sx={{
                                color:
                                COLORS.subtext,

                                letterSpacing:
                                    ".08em",
                            }}
                        >
                            {texts.currentBalance}
                        </Typography>

                        <Typography
                            variant="h2"
                            mt={0.5}
                            fontWeight={800}
                            sx={{
                                color:
                                COLORS.navy,
                            }}
                        >
                            {formatMoney(
                                currentBalance,
                                currency,
                                language
                            )}
                        </Typography>

                        <Chip
                            label={
                                `${texts.balanceDate}: ${formatDate(
                                    reconciliation
                                        ?.balanceDate,
                                    language
                                )}`
                            }
                            size="small"
                            sx={{
                                mt:
                                    1.5,

                                background:
                                COLORS.blueLight,

                                color:
                                COLORS.blue,

                                fontWeight:
                                    700,
                            }}
                        />
                    </Box>
                </Card>

                {/* ================================================= */}
                {/* SUCCESS */}
                {/* ================================================= */}

                {
                    success ? (
                        <Card
                            sx={
                                cardSx
                            }
                        >
                            <Box
                                p={{
                                    xs:
                                        3,

                                    md:
                                        5,
                                }}
                                textAlign="center"
                            >
                                <Box
                                    width={78}
                                    height={78}
                                    borderRadius="10px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mx="auto"
                                    sx={{
                                        background:
                                        resultConfig
                                            ?.background,

                                        color:
                                        resultConfig
                                            ?.color,
                                    }}
                                >
                                    {
                                        resultConfig
                                            ?.icon
                                    }
                                </Box>

                                <Typography
                                    variant="h4"
                                    fontWeight={800}
                                    mt={2}
                                >
                                    {texts.thankYou}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    mt={1}
                                    sx={{
                                        color:
                                        COLORS.subtext,
                                    }}
                                >
                                    {
                                        success.type ===
                                        "agreed"
                                            ? texts.agreedSuccess
                                            : texts.disputedSuccess
                                    }
                                </Typography>

                                <Alert
                                    severity="success"
                                    sx={{
                                        mt:
                                            3,

                                        textAlign:
                                            "left",

                                        borderRadius:
                                            "8px",
                                    }}
                                >
                                    {texts.recorded}
                                </Alert>

                                <Box
                                    mt={3}
                                    p={2.5}
                                    borderRadius="8px"
                                    sx={{
                                        background:
                                            "#F8FAFC",

                                        border:
                                            `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <InfoRow
                                        label={
                                            texts.answer
                                        }
                                        value={
                                            resultConfig
                                                ?.label
                                        }
                                    />

                                    <Divider />

                                    <InfoRow
                                        label={
                                            texts.answeredBy
                                        }
                                        value={
                                            success
                                                ?.respondedBy
                                        }
                                    />

                                    <Divider />

                                    <InfoRow
                                        label={
                                            texts.answeredAt
                                        }
                                        value={
                                            formatDateTime(
                                                success
                                                    ?.respondedAt,
                                                language
                                            )
                                        }
                                    />
                                </Box>
                            </Box>
                        </Card>
                    ) : (
                        <>
                            {/* ============================================= */}
                            {/* RESPONSE FORM */}
                            {/* ============================================= */}

                            <Card
                                sx={{
                                    ...cardSx,

                                    mb:
                                        2.5,
                                }}
                            >
                                <Box
                                    p={{
                                        xs:
                                            2.5,

                                        md:
                                            3.5,
                                    }}
                                >
                                    <Typography
                                        variant="h5"
                                        fontWeight={800}
                                    >
                                        {texts.responseTitle}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        mt={0.5}
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {texts.responseSubtitle}
                                    </Typography>

                                    {
                                        previousResponse ? (
                                            <Alert
                                                severity="info"
                                                sx={{
                                                    mt:
                                                        2,

                                                    borderRadius:
                                                        "8px",
                                                }}
                                            >
                                                {texts.previousResponse}
                                            </Alert>
                                        ) : null
                                    }

                                    <Grid
                                        container
                                        spacing={2}
                                        mt={0.5}
                                    >
                                        <Grid
                                            item
                                            xs={12}
                                            md={6}
                                        >
                                            <ResponseButton
                                                active={
                                                    responseType ===
                                                    "agreed"
                                                }
                                                type="agreed"
                                                texts={
                                                    texts
                                                }
                                                onClick={() =>
                                                    selectResponse(
                                                        "agreed"
                                                    )
                                                }
                                            />

                                            {
                                                responseType ===
                                                "agreed" ? (
                                                    <Typography
                                                        variant="caption"
                                                        display="block"
                                                        mt={1}
                                                        sx={{
                                                            color:
                                                            COLORS.green,
                                                        }}
                                                    >
                                                        {texts.agreedDescription}
                                                    </Typography>
                                                ) : null
                                            }
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            md={6}
                                        >
                                            <ResponseButton
                                                active={
                                                    responseType ===
                                                    "disputed"
                                                }
                                                type="disputed"
                                                texts={
                                                    texts
                                                }
                                                onClick={() =>
                                                    selectResponse(
                                                        "disputed"
                                                    )
                                                }
                                            />

                                            {
                                                responseType ===
                                                "disputed" ? (
                                                    <Typography
                                                        variant="caption"
                                                        display="block"
                                                        mt={1}
                                                        sx={{
                                                            color:
                                                            COLORS.red,
                                                        }}
                                                    >
                                                        {texts.disputedDescription}
                                                    </Typography>
                                                ) : null
                                            }
                                        </Grid>
                                    </Grid>

                                    {
                                        responseType ? (
                                            <>
                                                <Divider
                                                    sx={{
                                                        my:
                                                            3,
                                                    }}
                                                />

                                                {/* DISPUTED FIELDS */}

                                                {
                                                    responseType ===
                                                    "disputed" ? (
                                                        <Grid
                                                            container
                                                            spacing={2}
                                                            mb={2}
                                                        >
                                                            <Grid
                                                                item
                                                                xs={12}
                                                                md={5}
                                                            >
                                                                <TextField
                                                                    fullWidth
                                                                    type="number"
                                                                    label={
                                                                        texts.amount
                                                                    }
                                                                    value={
                                                                        form.balance
                                                                    }
                                                                    onChange={(
                                                                        event
                                                                    ) =>
                                                                        updateForm(
                                                                            "balance",
                                                                            event
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    sx={
                                                                        inputSx
                                                                    }
                                                                />
                                                            </Grid>

                                                            <Grid
                                                                item
                                                                xs={12}
                                                                md={3}
                                                            >
                                                                <TextField
                                                                    fullWidth
                                                                    value={
                                                                        currency
                                                                    }
                                                                    label={
                                                                        texts.currency
                                                                    }
                                                                    disabled
                                                                    sx={
                                                                        inputSx
                                                                    }
                                                                />
                                                            </Grid>

                                                            <Grid
                                                                item
                                                                xs={12}
                                                                md={4}
                                                            >
                                                                <FormControl
                                                                    fullWidth
                                                                    sx={
                                                                        inputSx
                                                                    }
                                                                >
                                                                    <InputLabel>
                                                                        {texts.balanceType}
                                                                    </InputLabel>
                                                                    <Select
                                                                        value={
                                                                            form.balanceType
                                                                        }
                                                                        label={
                                                                            texts.balanceType
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            updateForm(
                                                                                "balanceType",
                                                                                event.target.value
                                                                            )
                                                                        }
                                                                        MenuProps={{
                                                                            PaperProps: {
                                                                                sx: {
                                                                                    mt: 0.8,

                                                                                    borderRadius:
                                                                                        "8px",

                                                                                    border:
                                                                                        `1px solid ${COLORS.border}`,

                                                                                    boxShadow:
                                                                                        "0 10px 30px rgba(16,24,40,0.12)",

                                                                                    "& .MuiMenuItem-root": {
                                                                                        minHeight:
                                                                                            "42px",

                                                                                        fontSize:
                                                                                            "13px",

                                                                                        color:
                                                                                        COLORS.text,

                                                                                        "&:hover": {
                                                                                            backgroundColor:
                                                                                            COLORS.blueLight,
                                                                                        },

                                                                                        "&.Mui-selected": {
                                                                                            backgroundColor:
                                                                                            COLORS.blueLight,

                                                                                            color:
                                                                                            COLORS.blue,

                                                                                            fontWeight:
                                                                                                700,
                                                                                        },

                                                                                        "&.Mui-selected:hover": {
                                                                                            backgroundColor:
                                                                                            COLORS.blueLight,
                                                                                        },
                                                                                    },
                                                                                },
                                                                            },
                                                                        }}
                                                                    >
                                                                        <MenuItem value="debit">
                                                                            {texts.debit}
                                                                        </MenuItem>

                                                                        <MenuItem value="credit">
                                                                            {texts.credit}
                                                                        </MenuItem>
                                                                    </Select>
                                                                </FormControl>
                                                            </Grid>

                                                            <Grid
                                                                item
                                                                xs={12}
                                                            >
                                                                <TextField
                                                                    fullWidth
                                                                    multiline
                                                                    minRows={4}
                                                                    label={
                                                                        texts.explanation
                                                                    }
                                                                    placeholder={
                                                                        texts.explanationPlaceholder
                                                                    }
                                                                    value={
                                                                        form.note
                                                                    }
                                                                    onChange={(
                                                                        event
                                                                    ) =>
                                                                        updateForm(
                                                                            "note",
                                                                            event
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    sx={
                                                                        multilineSx
                                                                    }
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    ) : null
                                                }

                                                {/* DOCUMENT */}

                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        p:
                                                            2.2,

                                                        mb:
                                                            2,

                                                        borderRadius:
                                                            "8px",

                                                        borderStyle:
                                                            "dashed",

                                                        borderColor:
                                                        COLORS.borderDark,

                                                        background:
                                                            "#FAFBFC",
                                                    }}
                                                >
                                                    <Box
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems={{
                                                            xs:
                                                                "flex-start",

                                                            sm:
                                                                "center",
                                                        }}
                                                        flexDirection={{
                                                            xs:
                                                                "column",

                                                            sm:
                                                                "row",
                                                        }}
                                                        gap={2}
                                                    >
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={1.4}
                                                        >
                                                            <Box
                                                                width={42}
                                                                height={42}
                                                                borderRadius="11px"
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="center"
                                                                sx={{
                                                                    background:
                                                                    COLORS.blueLight,

                                                                    color:
                                                                    COLORS.blue,
                                                                }}
                                                            >
                                                                <CloudUpload />
                                                            </Box>

                                                            <Box>
                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={800}
                                                                >
                                                                    {texts.document}
                                                                </Typography>

                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color:
                                                                            responseType === "disputed"
                                                                                ? COLORS.red
                                                                                : COLORS.subtext,

                                                                        fontWeight:
                                                                            responseType === "disputed"
                                                                                ? 800
                                                                                : 400,
                                                                    }}
                                                                >
                                                                    {responseType === "disputed"
                                                                        ? texts.required
                                                                        : texts.optional}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        <input
                                                            ref={
                                                                fileInputRef
                                                            }
                                                            type="file"
                                                            hidden
                                                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                            onChange={
                                                                handleFileChange
                                                            }
                                                        />

                                                        <Button
                                                            variant="contained"
                                                            disableElevation
                                                            startIcon={
                                                                <InsertDriveFile
                                                                    sx={{
                                                                        fontSize:
                                                                            "18px !important",
                                                                    }}
                                                                />
                                                            }
                                                            onClick={() =>
                                                                fileInputRef
                                                                    .current
                                                                    ?.click()
                                                            }
                                                            sx={{
                                                                minWidth: "138px",
                                                                height: "42px",

                                                                px: 2.2,

                                                                borderRadius: "10px",

                                                                backgroundColor:
                                                                    `${COLORS.blue} !important`,

                                                                color:
                                                                    `${COLORS.white} !important`,

                                                                border:
                                                                    `1px solid ${COLORS.blue}`,

                                                                fontSize: "12px",
                                                                fontWeight: 800,

                                                                textTransform: "none",

                                                                boxShadow:
                                                                    "0 2px 5px rgba(23,92,211,0.18)",

                                                                "& .MuiButton-startIcon": {
                                                                    color:
                                                                        `${COLORS.white} !important`,
                                                                },

                                                                "& .MuiSvgIcon-root": {
                                                                    color:
                                                                        `${COLORS.white} !important`,
                                                                },

                                                                "&:hover": {
                                                                    backgroundColor:
                                                                        "#1849A9 !important",

                                                                    borderColor:
                                                                        "#1849A9",

                                                                    color:
                                                                        `${COLORS.white} !important`,

                                                                    boxShadow:
                                                                        "0 4px 10px rgba(23,92,211,0.25)",
                                                                },

                                                                "&:active": {
                                                                    backgroundColor:
                                                                        "#123B8F !important",
                                                                },

                                                                "&.Mui-disabled": {
                                                                    backgroundColor:
                                                                        "#D0D5DD !important",

                                                                    color:
                                                                        `${COLORS.white} !important`,
                                                                },
                                                            }}
                                                        >
                                                            {texts.selectDocument}
                                                        </Button>
                                                    </Box>

                                                    {
                                                        selectedFile ? (
                                                            <Box
                                                                mt={2}
                                                            >
                                                                <Alert
                                                                    severity="info"
                                                                    icon={
                                                                        <InsertDriveFile />
                                                                    }
                                                                    sx={{
                                                                        borderRadius:
                                                                            "10px",
                                                                    }}
                                                                >
                                                                    <strong>
                                                                        {texts.selectedDocument}:
                                                                    </strong>

                                                                    {" "}

                                                                    {
                                                                        selectedFile
                                                                            .name
                                                                    }
                                                                </Alert>

                                                                <Typography
                                                                    variant="caption"
                                                                    display="block"
                                                                    mt={1}
                                                                    sx={{
                                                                        color:
                                                                        COLORS.amber,
                                                                    }}
                                                                >
                                                                    {texts.documentUploadPending}
                                                                </Typography>
                                                            </Box>
                                                        ) : null
                                                    }
                                                </Paper>

                                                {/* RESPONDER */}

                                                <TextField
                                                    fullWidth
                                                    label={
                                                        texts.responder
                                                    }
                                                    placeholder={
                                                        texts.responderPlaceholder
                                                    }
                                                    value={
                                                        form.respondedBy
                                                    }
                                                    onChange={(
                                                        event
                                                    ) =>
                                                        updateForm(
                                                            "respondedBy",
                                                            event
                                                                .target
                                                                .value
                                                        )
                                                    }
                                                    InputProps={{
                                                        startAdornment:
                                                            (
                                                                <Person
                                                                    sx={{
                                                                        mr:
                                                                            1,

                                                                        color:
                                                                        COLORS.muted,
                                                                    }}
                                                                />
                                                            ),
                                                    }}
                                                    sx={
                                                        inputSx
                                                    }
                                                />

                                                {
                                                    formError ? (
                                                        <Alert
                                                            severity="error"
                                                            sx={{
                                                                mt:
                                                                    2,

                                                                borderRadius:
                                                                    "8px",
                                                            }}
                                                        >
                                                            {formError}
                                                        </Alert>
                                                    ) : null
                                                }

                                                {/* SUBMIT */}

                                                <Box
                                                    display="flex"
                                                    justifyContent="flex-end"
                                                    mt={2.5}
                                                >
                                                    <Button
                                                        variant="contained"
                                                        size="large"
                                                        startIcon={
                                                            submitting ? (
                                                                <CircularProgress
                                                                    size={18}
                                                                    color="inherit"
                                                                />
                                                            ) : (
                                                                <Send />
                                                            )
                                                        }
                                                        disabled={
                                                            submitting ||
                                                            fileUploading
                                                        }
                                                        onClick={
                                                            handleSubmit
                                                        }
                                                        sx={{
                                                            minWidth:
                                                                190,

                                                            minHeight:
                                                                48,

                                                            borderRadius:
                                                                "11px",

                                                            background:
                                                                responseType ===
                                                                "agreed"
                                                                    ? COLORS.greenStrong
                                                                    : COLORS.redStrong,

                                                            fontWeight:
                                                                800,

                                                            "&:hover":
                                                                {
                                                                    background:
                                                                        responseType ===
                                                                        "agreed"
                                                                            ? COLORS.green
                                                                            : COLORS.red,
                                                                },
                                                        }}
                                                    >
                                                        {
                                                            submitting ||
                                                            fileUploading
                                                                ? texts.sending
                                                                : texts.submit
                                                        }
                                                    </Button>
                                                </Box>
                                            </>
                                        ) : null
                                    }
                                </Box>
                            </Card>

                            {/* ============================================= */}
                            {/* HISTORY */}
                            {/* ============================================= */}

                            <Card
                                sx={
                                    cardSx
                                }
                            >
                                <Box
                                    p={{
                                        xs:
                                            2.5,

                                        md:
                                            3,
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        gap={2}
                                    >
                                        <Typography
                                            variant="h6"
                                            fontWeight={800}
                                        >
                                            {texts.responseHistory}
                                        </Typography>

                                        <Button
                                            variant="text"
                                            disabled={
                                                historyLoading
                                            }
                                            onClick={
                                                showHistory
                                                    ? () =>
                                                        setShowHistory(
                                                            false
                                                        )
                                                    : fetchHistory
                                            }
                                        >
                                            {
                                                historyLoading ? (
                                                    <CircularProgress
                                                        size={17}
                                                    />
                                                ) : (
                                                    showHistory
                                                        ? "-"
                                                        : "+"
                                                )
                                            }
                                        </Button>
                                    </Box>

                                    {
                                        showHistory ? (
                                            <Box
                                                mt={2}
                                            >
                                                {
                                                    history.length ===
                                                    0 ? (
                                                        <Alert
                                                            severity="info"
                                                            sx={{
                                                                borderRadius:
                                                                    "10px",
                                                            }}
                                                        >
                                                            {texts.noHistory}
                                                        </Alert>
                                                    ) : (
                                                        history.map(
                                                            (
                                                                item,
                                                                index
                                                            ) => (
                                                                <Paper
                                                                    key={
                                                                        item
                                                                            ?.id ||
                                                                        `${item.respondedAt}-${index}`
                                                                    }
                                                                    variant="outlined"
                                                                    sx={{
                                                                        p:
                                                                            2,

                                                                        mb:
                                                                            1.2,

                                                                        borderRadius:
                                                                            "8px",

                                                                        borderColor:
                                                                        COLORS.border,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        display="flex"
                                                                        justifyContent="space-between"
                                                                        alignItems="center"
                                                                        gap={2}
                                                                        flexWrap="wrap"
                                                                    >
                                                                        <Chip
                                                                            size="small"
                                                                            label={
                                                                                item.response ===
                                                                                "agreed"
                                                                                    ? texts.agreed
                                                                                    : texts.disputed
                                                                            }
                                                                            sx={{
                                                                                background:
                                                                                    item.response ===
                                                                                    "agreed"
                                                                                        ? COLORS.greenLight
                                                                                        : COLORS.redLight,

                                                                                color:
                                                                                    item.response ===
                                                                                    "agreed"
                                                                                        ? COLORS.green
                                                                                        : COLORS.red,

                                                                                fontWeight:
                                                                                    700,
                                                                            }}
                                                                        />

                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                color:
                                                                                COLORS.subtext,
                                                                            }}
                                                                        >
                                                                            {formatDateTime(
                                                                                item.respondedAt,
                                                                                language
                                                                            )}
                                                                        </Typography>
                                                                    </Box>

                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight={700}
                                                                        mt={1.2}
                                                                    >
                                                                        {item.respondedBy ||
                                                                            "-"}
                                                                    </Typography>

                                                                    {
                                                                        item.response ===
                                                                        "disputed" ? (
                                                                            <Box
                                                                                mt={1}
                                                                            >
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    display="block"
                                                                                    sx={{
                                                                                        color:
                                                                                        COLORS.subtext,
                                                                                    }}
                                                                                >
                                                                                    {texts.amount}
                                                                                </Typography>

                                                                                <Typography
                                                                                    variant="body2"
                                                                                    fontWeight={700}
                                                                                >
                                                                                    {formatMoney(
                                                                                        item.balance,
                                                                                        item.currency ||
                                                                                        currency,
                                                                                        language
                                                                                    )}

                                                                                    {" • "}

                                                                                    {
                                                                                        item.balanceType ===
                                                                                        "credit"
                                                                                            ? texts.credit
                                                                                            : texts.debit
                                                                                    }
                                                                                </Typography>

                                                                                {
                                                                                    item.note ? (
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            mt={1}
                                                                                            sx={{
                                                                                                color:
                                                                                                COLORS.subtext,
                                                                                            }}
                                                                                        >
                                                                                            {item.note}
                                                                                        </Typography>
                                                                                    ) : null
                                                                                }
                                                                            </Box>
                                                                        ) : null
                                                                    }
                                                                </Paper>
                                                            )
                                                        )
                                                    )
                                                }
                                            </Box>
                                        ) : null
                                    }
                                </Box>
                            </Card>
                        </>
                    )
                }

                {/* ================================================= */}
                {/* FOOTER */}
                {/* ================================================= */}

                <Box
                    textAlign="center"
                    py={3}
                >
                    <Typography
                        variant="caption"
                        display="block"
                        sx={{
                            color:
                            COLORS.muted,
                        }}
                    >
                        {texts.privacy}
                    </Typography>

                    <Typography
                        variant="caption"
                        display="block"
                        mt={0.5}
                        sx={{
                            color:
                            COLORS.muted,
                        }}
                    >
                        © {new Date().getFullYear()} Filenders Logistics & Technology
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default ReconciliationResponse;